import {
  BarChart3,
  Bell,
  Bug,
  CalendarRange,
  ChevronDown,
  CreditCard,
  Database,
  FlaskConical,
  FolderTree,
  Gauge,
  Home,
  Inbox,
  LifeBuoy,
  LineChart,
  LogOut,
  Monitor,
  Moon,
  MousePointerClick,
  Pin,
  PinOff,
  Play,
  Settings,
  Shield,
  Sun,
  User,
  ListChecks,
} from "lucide-react";
import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { ScopeSwitcherRail } from "./sidebar/ScopeSwitcherRail";
import { LogoutConfirmDialog } from "./sidebar/SidebarItem";

import shipFasterImg from "@/assets/icons/ship_faster2.png";
import { useTour, type TourId } from "@/common/components/tour";
import { FixedMenuItems, SupportMenuItems } from "@/common/constants/MenuItems";
import { useAuth } from "@/common/hooks/useAuth";
import { useNotifications } from "@/common/hooks/useNotifications";
import { usePermissions } from "@/common/hooks/usePermissions";
import { isPathActive } from "@/common/utils/utils";
import PlanUsageCard from "@/components/billing/PlanUsageCard";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import CircularLogo from "@/components/ui/CircularLogo";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useTheme } from "@/themes/ThemeProvider";

/* ─────────────────────────── constants ─────────────────────────── */

const PIN_KEY = "smart_sidebar_pinned";
const WIDTH_TRANSITION =
  "transition-[width] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)] will-change-[width] motion-reduce:transition-none";
const tooltipDark =
  "z-[1200] border border-rail-border bg-rail text-[12px] font-medium text-rail-foreground shadow-md";

type Perm = { resource: string; action: string };

type NavItem = {
  label: string;
  path: string;
  icon: any;
  matchPattern?: string;
  requiredPermission?: Perm;
};

type NavSection = { title?: string; items: NavItem[] };

/* Grouped navigation — mirrors the product menu with section grouping. */
const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      {
        label: "Dashboard",
        path: "/dashboard",
        icon: Home,
        matchPattern: "^/(dashboard)?$",
      },
    ],
  },
  {
    title: "Design",
    items: [
      {
        label: "Test Cases",
        path: "/testcases/all",
        icon: FlaskConical,
        matchPattern: "^/testcases/(?!performance).*$",
      },
      {
        label: "UI Elements",
        path: "/elements",
        icon: MousePointerClick,
        requiredPermission: { resource: "ELEMENTS", action: "READ" },
      },
      {
        label: "Requirements",
        path: "/test-management/requirements",
        icon: ListChecks,
        requiredPermission: { resource: "REQUIREMENTS", action: "READ" },
      },
      {
        label: "Test Data",
        path: "/test-data",
        icon: Database,
        requiredPermission: { resource: "TEST_DATA", action: "READ" },
      },
      {
        label: "Upload",
        path: "/uploads",
        icon: FolderTree,
        requiredPermission: { resource: "UPLOADS", action: "READ" },
      },
    ],
  },
  {
    title: "Execution",
    items: [
      {
        label: "Cycles",
        path: "/cycles",
        icon: CalendarRange,
        matchPattern: "^/cycles(?!/queue)(/.*)?$",
      },
      { label: "My Queue", path: "/cycles/queue", icon: Inbox },
    ],
  },
  {
    title: "Load Testing",
    items: [
      {
        label: "Management",
        path: "/loadtest/library",
        icon: Gauge,
        matchPattern:
          "^/(loadtest/library|testcases/performance|load-testing)(/.*)?$",
      },
      {
        label: "Executions",
        path: "/loadtest/executions",
        icon: Play,
        matchPattern:
          "^/(loadtest/executions|testcases/executions|executions)(/.*)?$",
      },
    ],
  },
  {
    title: "Quality & Reporting",
    items: [
      { label: "Defects", path: "/defects", icon: Bug },
      {
        label: "Reports",
        path: "/reports",
        icon: BarChart3,
        matchPattern: "^/reports/?$",
        requiredPermission: { resource: "REPORTS", action: "READ" },
      },
      {
        label: "Analytics",
        path: "/reports/analytics",
        icon: LineChart,
        requiredPermission: { resource: "REPORTS", action: "READ" },
      },
    ],
  },
];

/* Developers have no dashboard — their landing IS My Queue. Surface "My Queue"
 * at the top (in place of Dashboard) and drop it from the Execution group so it
 * is never duplicated. Every other role keeps the default layout untouched. */
function sectionsForRole(isDeveloper: boolean): NavSection[] {
  if (!isDeveloper) return NAV_SECTIONS;
  const myQueue: NavItem = {
    label: "My Queue",
    path: "/cycles/queue",
    icon: Inbox,
  };
  return NAV_SECTIONS.map((section) => {
    if (!section.title) {
      // Top (untitled) group: replace Dashboard with My Queue.
      return {
        items: section.items.map((it) =>
          it.path === "/dashboard" ? myQueue : it,
        ),
      };
    }
    if (section.title === "Execution") {
      // Remove the now-promoted My Queue from the Execution group.
      return {
        ...section,
        items: section.items.filter((it) => it.path !== "/cycles/queue"),
      };
    }
    return section;
  });
}

function tourForRoute(pathname: string): TourId {
  if (pathname.startsWith("/elements")) return "first-element";
  if (pathname.startsWith("/testcases")) return "first-scenario";
  if (pathname.startsWith("/test-management/test-results"))
    return "first-failure";
  return "first-login";
}

const slugify = (label: string) => label.toLowerCase().replace(/\s+/g, "-");

/* ─────────────────────────── shared row styles ─────────────────────────── */

const ROW =
  "relative flex h-8 items-center overflow-hidden rounded-md outline-none transition-colors duration-150";
const ROW_IDLE =
  "text-rail-muted hover:bg-rail-foreground/[0.08] hover:text-rail-foreground";
const ROW_ACTIVE =
  "bg-rail-foreground/[0.08] text-rail-foreground";

function IconSlot({ icon: Icon }: { icon: any }) {
  // Fixed icon column === the collapsed rail's inner width (53 − 2×8 padding),
  // so the icon stays centered in the rail and never jumps between states.
  return (
    <span className="grid h-10 w-[37px] shrink-0 place-items-center">
      <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
    </span>
  );
}

function RowLabel({ children, expanded }: { children: React.ReactNode; expanded?: boolean }) {
  return (
    <span
      className={cn(
        "w-[155px] shrink-0 whitespace-nowrap pr-2.5 text-left text-[13px] transition-opacity duration-200",
        expanded === false ? "opacity-0" : "opacity-100"
      )}
    >
      {children}
    </span>
  );
}

/* ─────────────────────────── pinned state ─────────────────────────── */

function usePinned() {
  const [pinned, setPinned] = useState<boolean>(() => {
    try {
      return localStorage.getItem(PIN_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggle = useCallback(() => {
    setPinned((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(PIN_KEY, next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);
  return { pinned, toggle };
}

/* ─────────────────────────── leaf nav item ─────────────────────────── */

function NavLeaf({
  item,
  expanded,
  onHover,
}: {
  item: NavItem;
  expanded: boolean;
  onHover?: () => void;
}) {
  const { pathname } = useLocation();
  const active = isPathActive(item.path, pathname, item.matchPattern);

  const link = (
    <Link
      to={item.path}
      data-tour={`sidebar-${slugify(item.label)}`}
      aria-label={item.label}
      onMouseEnter={onHover}
      className={cn(ROW, active ? ROW_ACTIVE : ROW_IDLE)}
    >
      <IconSlot icon={item.icon} />
      <RowLabel expanded={expanded}>{item.label}</RowLabel>
    </Link>
  );

  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (expanded) setOpen(false);
  }, [expanded]);

  return (
    <Tooltip 
      open={expanded ? false : open} 
      onOpenChange={(newOpen) => {
        if (expanded && newOpen) return;
        setOpen(newOpen);
      }}
    >
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={12} className={tooltipDark}>
        {item.label}
      </TooltipContent>
    </Tooltip>
  );
}

/* ─────────────────────────── section ─────────────────────────── */

/*
 * Groups are FLAT and always-visible — no accordion. Because the sidebar only
 * animates its width (icons keep a fixed Y position), items never shift
 * vertically between collapsed and expanded. The group header is a fixed-height
 * row in both states (caption when expanded, a subtle centered divider when
 * collapsed) so the icons below never jump. This eliminates cursor-chasing and
 * layout shift entirely.
 */
function NavSectionBlock({
  section,
  expanded,
  onItemHover,
}: {
  section: NavSection;
  expanded: boolean;
  onItemHover?: () => void;
}) {
  const { can } = usePermissions();
  const items = section.items.filter(
    (it) =>
      !it.requiredPermission ||
      can(
        it.requiredPermission.resource as any,
        it.requiredPermission.action as any,
      ),
  );
  if (items.length === 0) return null;

  const list = (
    <div className="flex flex-col gap-px">
      {items.map((it) => (
        <NavLeaf
          key={it.label}
          item={it}
          expanded={expanded}
          onHover={onItemHover}
        />
      ))}
    </div>
  );

  // Dashboard (no title) — render items directly.
  if (!section.title) return list;

  return (
    <div className="flex flex-col">
      {/* Fixed-height group label — same height collapsed/expanded so icons never move. */}
      <div className="flex h-9 items-center pt-3 pb-1">
        {expanded ? (
          <span className="truncate px-2.5 text-[10.5px] font-semibold uppercase tracking-[0.09em] text-rail-muted/80">
            {section.title}
          </span>
        ) : (
          <span className="mx-auto h-px w-6 rounded-full bg-rail-border" />
        )}
      </div>
      {list}
    </div>
  );
}

/* ─────────────────────────── expandable footer group ─────────────────────────── */

type SubItem = {
  label: string;
  path: string;
  icon?: any;
  matchPattern?: string;
};

/*
 * Footer groups with sub-items (Configuration, Support):
 *  - Expanded sidebar → click the row to expand/collapse the submenu INLINE
 *    (accordion), just like a normal menu ▸ submenu. The chevron rotates.
 *  - Collapsed rail → there is no room for an inline submenu, so hovering the
 *    icon opens a portalled flyout panel (with hover forgiveness) beside the
 *    rail. Clicking still navigates to the group's landing page.
 */
function FooterGroup({
  icon: Icon,
  title,
  subs,
  expanded,
  fallbackPath,
  onSelect,
  tourId,
  onHover,
}: {
  icon: any;
  title: string;
  subs: SubItem[];
  expanded: boolean;
  fallbackPath: string;
  onSelect: (sub: SubItem) => void;
  tourId?: string;
  onHover?: () => void;
}) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const anyActive = useMemo(
    () =>
      subs.some(
        (s) =>
          s.path !== "#tour" && isPathActive(s.path, pathname, s.matchPattern),
      ),
    [subs, pathname],
  );
  const [open, setOpen] = useState(anyActive);

  const subList = (
    <div className="flex flex-col gap-px">
      {subs.map((sub) => {
        const active =
          sub.path !== "#tour" &&
          isPathActive(sub.path, pathname, sub.matchPattern);
        return (
          <button
            key={sub.label}
            data-tour={sub.path === "#tour" ? "restart-tour" : undefined}
            onClick={() => onSelect(sub)}
            className={cn(
              "flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-[13px] transition-colors",
              active
                ? "bg-rail-foreground/15 text-rail-foreground font-semibold"
                : "text-rail-foreground/80 hover:bg-rail-foreground/[0.08] hover:text-rail-foreground",
            )}
          >
            {sub.icon && (
              <sub.icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
            )}
            <span className="w-[155px] shrink-0 whitespace-nowrap tracking-tight font-medium">
              {sub.label}
            </span>
          </button>
        );
      })}
    </div>
  );

  const [hoverOpen, setHoverOpen] = useState(false);
  
  const collapsedView = (
    <div
      className={cn(
        "transition-opacity duration-200",
        expanded ? "pointer-events-none absolute inset-0 opacity-0" : "relative opacity-100"
      )}
    >
      <HoverCard open={expanded ? false : hoverOpen} onOpenChange={setHoverOpen} openDelay={90} closeDelay={280}>
        <HoverCardTrigger asChild>
          <button
            data-tour={tourId}
            onClick={() => navigate(fallbackPath)}
            onMouseEnter={onHover}
            aria-label={title}
            className={cn(ROW, anyActive ? ROW_ACTIVE : ROW_IDLE)}
          >
            <IconSlot icon={Icon} />
          </button>
        </HoverCardTrigger>
        <HoverCardContent
          side="right"
          align="start"
          sideOffset={8}
          collisionPadding={8}
          className="w-60 rounded-lg border border-rail-border bg-rail p-1.5 text-rail-foreground sidebar-shadow"
        >
          <div className="px-2 pb-1 pt-0.5 text-[10px] font-semibold uppercase tracking-[0.09em] text-rail-muted/60">
            {title}
          </div>
          {subList}
        </HoverCardContent>
      </HoverCard>
    </div>
  );

  const expandedView = (
    <div
      className={cn(
        "transition-opacity duration-200",
        expanded ? "relative opacity-100" : "pointer-events-none absolute inset-0 opacity-0"
      )}
    >
      <Collapsible
        open={open}
        onOpenChange={setOpen}
        className="group/col flex flex-col w-full"
      >
        <CollapsibleTrigger asChild>
          <button
            data-tour={tourId}
            onMouseEnter={onHover}
            className={cn(ROW, anyActive ? ROW_ACTIVE : ROW_IDLE)}
          >
            <IconSlot icon={Icon} />
            <RowLabel expanded={expanded}>{title}</RowLabel>
            <ChevronDown
              className="mr-2.5 h-3.5 w-3.5 shrink-0 text-rail-muted transition-transform duration-200 group-data-[state=closed]/col:-rotate-90"
              strokeWidth={2}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden w-full">
          <div className="ml-[26px] mt-px flex flex-col gap-px border-l border-rail-border pl-2">
            {subList}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );

  return (
    <div className="relative w-full">
      {collapsedView}
      {expandedView}
    </div>
  );
}

/* ─────────────────────────── notifications ─────────────────────────── */

function NotificationsRow({ expanded }: { expanded: boolean }) {
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();
  const hasUnread = unreadCount > 0;
  const countLabel = unreadCount > 99 ? "99+" : String(unreadCount);

  const button = (
    <button
      onClick={() => navigate("/notifications")}
      aria-label="Notifications"
      className={cn(ROW, ROW_IDLE, "w-full")}
    >
      <span className="relative grid h-8 w-[37px] shrink-0 place-items-center">
        <Bell className="h-[18px] w-[18px]" strokeWidth={1.75} />
        {hasUnread && !expanded && (
          <span className="absolute right-1.5 top-1.5 inline-flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-rail-foreground px-0.5 text-[8px] font-bold tabular-nums text-rail">
            {countLabel}
          </span>
        )}
      </span>
      <span
        className={cn(
          "flex w-[155px] shrink-0 items-center whitespace-nowrap pr-2.5 transition-opacity duration-200",
          expanded ? "opacity-100" : "opacity-0"
        )}
      >
        <RowLabel expanded={expanded}>Notifications</RowLabel>
        {hasUnread && (
          <span className="mr-2.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rail-foreground px-1.5 text-[10px] font-semibold tabular-nums text-rail">
            {countLabel}
          </span>
        )}
      </span>
    </button>
  );

  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (expanded) setOpen(false);
  }, [expanded]);

  return (
    <Tooltip 
      open={expanded ? false : open} 
      onOpenChange={(newOpen) => {
        if (expanded && newOpen) return;
        setOpen(newOpen);
      }}
    >
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={12} className={tooltipDark}>
        Notifications{hasUnread ? ` · ${countLabel}` : ""}
      </TooltipContent>
    </Tooltip>
  );
}

function ThemeRow({ expanded }: { expanded: boolean }) {
  const { theme, resolvedTheme, setTheme } = useTheme();

  const effective = theme === "system" ? resolvedTheme : theme;
  const toggleTo = effective === "dark" ? "light" : "dark";
  const title =
    theme === "system"
      ? `Theme: System (${resolvedTheme})`
      : `Theme: ${theme === "dark" ? "Dark" : "Light"}`;

  const Icon = theme === "system" ? Monitor : effective === "dark" ? Moon : Sun;

  const button = (
    <div
      data-sidebar-no-expand="true"
      className="-mx-2 px-2"
      onMouseEnter={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
    >
      <button
        data-sidebar-no-expand="true"
        onMouseEnter={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          setTheme(toggleTo);
        }}
        aria-label={title}
        title={title}
        className={cn(ROW, ROW_IDLE, "w-full")}
      >
        <span className="relative grid h-8 w-[37px] shrink-0 place-items-center">
          <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
        </span>
        <span
          className={cn(
            "flex min-w-0 flex-1 items-center justify-between pr-2.5 transition-opacity duration-200",
            expanded ? "opacity-100" : "opacity-0"
          )}
        >
          <span className="text-left text-[13px] font-medium">Theme</span>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-rail-muted">
            {theme === "system"
              ? "System"
              : effective === "dark"
                ? "Dark"
                : "Light"}
          </span>
        </span>
      </button>
    </div>
  );

  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (expanded) setOpen(false);
  }, [expanded]);

  return (
    <Tooltip 
      open={expanded ? false : open} 
      onOpenChange={(newOpen) => {
        if (expanded && newOpen) return;
        setOpen(newOpen);
      }}
    >
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={12} className={tooltipDark}>
        {title}
      </TooltipContent>
    </Tooltip>
  );
}

/* ─────────────────────────── user menu ─────────────────────────── */

function UserRow({
  expanded,
  onMenuOpenChange,
}: {
  expanded: boolean;
  onMenuOpenChange: (open: boolean) => void;
}) {
  const { user, logoutUser, isSuperAdmin } = useAuth();
  const { isAdmin: isOrgAdmin, roleName } = usePermissions();
  const navigate = useNavigate();
  const [showLogout, setShowLogout] = useState(false);

  const displayName = user?.firstName
    ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ""}`
    : user?.name || "User";
  const email = user?.email || "";
  const initials = displayName
    .split(" ")
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);

  // Backend returns the effective project role: Test Manager, Tester, Test Lead,
  // Developer, Project Manager. The only path that returns "Admin" is the platform
  // super-admin branch, so surface that as "Super Admin".
  const normalizedRole = roleName?.trim();
  const roleLabel =
    isSuperAdmin || normalizedRole === "Admin"
      ? "Super Admin"
      : normalizedRole
        ? normalizedRole
        : null;

  return (
    <>
      {showLogout && (
        <LogoutConfirmDialog
          onConfirm={() => {
            setShowLogout(false);
            logoutUser();
          }}
          onCancel={() => setShowLogout(false)}
        />
      )}
      <DropdownMenu onOpenChange={onMenuOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="account-menu-trigger"
            title={displayName}
            aria-label="Account menu"
            className="flex h-11 w-full items-center rounded-md transition-colors hover:bg-rail-foreground/[0.08]"
          >
            <span className="grid h-11 w-[37px] shrink-0 place-items-center">
              <Avatar className="h-7 w-7 rounded-md">
                <AvatarFallback className="rounded-md bg-rail-foreground/15 text-[10px] font-bold text-rail-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </span>
            <div
              className={cn(
                "grid w-[155px] shrink-0 pr-2.5 text-left leading-tight transition-opacity duration-200",
                expanded ? "opacity-100" : "opacity-0 pointer-events-none"
              )}
            >
              <span className="truncate text-[12.5px] font-semibold text-rail-foreground">
                {displayName}
              </span>
              <span className="truncate text-[10.5px] text-rail-muted">
                {email}
              </span>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="right"
          align="end"
          sideOffset={10}
          className="z-[1200] w-64 overflow-hidden rounded-xl border border-rail-border sidebar-gradient p-1 text-rail-foreground sidebar-shadow"
        >
          <DropdownMenuLabel className="p-0 font-normal">
            <div className="flex items-center gap-2.5 px-2 py-2">
              <Avatar className="h-9 w-9 rounded-lg">
                <AvatarFallback className="rounded-lg bg-rail-foreground/15 text-[11px] font-bold text-rail-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="grid min-w-0 flex-1 leading-tight">
                <span className="truncate text-[12.5px] font-semibold text-rail-foreground">
                  {displayName}
                </span>
                <span className="truncate text-[10.5px] text-rail-muted">
                  {email}
                </span>
                {roleLabel && (
                  <span className="mt-1 inline-flex w-fit max-w-full items-center gap-1 truncate rounded-full bg-rail-foreground/10 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-rail-foreground/70">
                    <Shield className="h-2.5 w-2.5 shrink-0" />
                    {roleLabel}
                  </span>
                )}
              </div>
            </div>
          </DropdownMenuLabel>
          <div className="px-1 pb-1">
            <PlanUsageCard collapsed={false} dark />
          </div>
          <DropdownMenuSeparator className="bg-rail-border" />
          <DropdownMenuItem
            onClick={() => navigate("/settings/my-profile")}
            className="cursor-pointer text-[12.5px] text-rail-foreground/85 focus:bg-rail-foreground/[0.08] focus:text-rail-foreground"
          >
            <User className="mr-2 h-4 w-4" />
            Profile
          </DropdownMenuItem>
          {isOrgAdmin && (roleName ?? "").toLowerCase() !== "developer" && (
            <>
              <DropdownMenuItem
                onClick={() => navigate("/org-admin")}
                className="cursor-pointer text-[12.5px] text-rail-foreground/85 focus:bg-rail-foreground/[0.08] focus:text-rail-foreground"
              >
                <Shield className="mr-2 h-4 w-4" />
                Organisation Admin
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate("/org-admin/billing")}
                className="cursor-pointer text-[12.5px] text-rail-foreground/85 focus:bg-rail-foreground/[0.08] focus:text-rail-foreground"
              >
                <CreditCard className="mr-2 h-4 w-4" />
                Billing & Plan
              </DropdownMenuItem>
            </>
          )}
          {isSuperAdmin && (
            <>
              <DropdownMenuSeparator className="bg-rail-border" />
              <DropdownMenuItem
                onClick={() => navigate("/admin")}
                className="cursor-pointer text-[12.5px] text-violet-300 focus:bg-violet-500/15 focus:text-violet-200"
              >
                <Shield className="mr-2 h-4 w-4" />
                Super Admin Panel
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator className="bg-rail-border" />
          <DropdownMenuItem
            data-testid="logout-button"
            onClick={() => setShowLogout(true)}
            className="cursor-pointer text-[12.5px] text-red-400 focus:bg-red-500/15 focus:text-red-300"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}

/* ─────────────────────────── header ─────────────────────────── */

function SidebarHeader({
  expanded,
  pinned,
  togglePinned,
}: {
  expanded: boolean;
  pinned: boolean;
  togglePinned: () => void;
}) {
  return (
    <div className="flex h-14 items-center">
      <Link
        to="/dashboard"
        data-tour="sidebar-header"
        title="AutopilotQA"
        className="flex min-w-0 flex-1 items-center"
      >
        <span className="grid h-12 w-[37px] shrink-0 place-items-center">
          <CircularLogo size={28} animated={false} />
        </span>
        <span
          className={cn(
            "w-[155px] shrink-0 whitespace-nowrap text-[18px] font-semibold tracking-tight transition-opacity duration-200",
            expanded ? "opacity-100" : "opacity-0"
          )}
        >
          <span className="text-rail-foreground">Autopilot</span>{" "}
          <span className="text-brand-500">QA</span>
        </span>
      </Link>
      {/* Pin button appears only when expanded (hover or pinned) — never in the bare collapsed rail. */}
      <button
        onClick={togglePinned}
        aria-label={pinned ? "Unpin sidebar" : "Pin sidebar open"}
        title={pinned ? "Unpin sidebar" : "Pin sidebar open"}
        className={cn(
          "mr-1 grid h-8 w-8 shrink-0 place-items-center rounded-md transition-all duration-200",
          pinned
            ? "bg-rail-foreground/15 text-rail-foreground hover:bg-rail-foreground/20"
            : "text-rail-muted hover:bg-rail-foreground/10 hover:text-rail-foreground",
          expanded ? "opacity-100" : "opacity-0 pointer-events-none"
        )}
      >
        {pinned ? (
          <PinOff className="h-4 w-4" strokeWidth={1.75} />
        ) : (
          <Pin className="h-4 w-4" strokeWidth={1.75} />
        )}
      </button>
    </div>
  );
}

/* ─────────────────────────── shell ─────────────────────────── */

export function SmartSidebar() {
  const { pinned, toggle } = usePinned();
  const [hovered, setHovered] = useState(false);
  // Track each overlay independently (project switcher + profile menu). These
  // popovers can overlap: opening one while another closes fires open(true) and
  // close(false) in an unpredictable order. A single shared boolean would get
  // clobbered to `false` mid-transition and unfreeze the sidebar while a popover
  // is still open → hover fluctuation. Independent flags can never drift.
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frozenExpanded = useRef(false);

  const menuOpen = switcherOpen || profileOpen;
  // Base expand state: pinned (solid) or hover (overlay).
  const baseExpanded = pinned || hovered;
  // While any overlay is open we freeze the sidebar at whatever expand state it
  // had when the FIRST overlay opened — clicking the profile/switcher must only
  // open the popover, never collapse an expanded sidebar nor expand a collapsed
  // rail, even as the mouse moves onto a portalled popover outside the rail.
  const expanded = menuOpen ? frozenExpanded.current : baseExpanded;
  const overlay = !pinned && expanded;

  const handleSwitcherOpenChange = useCallback(
    (open: boolean) => {
      // Capture the freeze snapshot only on the rising edge from "no overlay".
      if (open && !switcherOpen && !profileOpen)
        frozenExpanded.current = pinned || hovered;
      setSwitcherOpen(open);
    },
    [pinned, hovered, switcherOpen, profileOpen],
  );
  const handleProfileOpenChange = useCallback(
    (open: boolean) => {
      if (open && !switcherOpen && !profileOpen)
        frozenExpanded.current = pinned || hovered;
      setProfileOpen(open);
    },
    [pinned, hovered, switcherOpen, profileOpen],
  );

  const triggerHover = useCallback(() => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    if (!pinned) {
      setHovered(true);
    }
  }, [pinned]);

  const onLeave = useCallback(() => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => setHovered(false), 60);
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const { startTour, resetTour } = useTour();
  const { can, isAdmin, roleName } = usePermissions();
  const isDeveloper = (roleName ?? "").toLowerCase() === "developer";
  const navSections = useMemo(
    () => sectionsForRole(isDeveloper),
    [isDeveloper],
  );

  const config = FixedMenuItems[0];
  const configSubs = (config.submenu ?? []).filter((sub: any) => {
    if (
      sub.path === "/settings/roles" &&
      !isAdmin &&
      !can("ROLES" as any, "READ" as any)
    )
      return false;
    if (
      sub.requiredPermission &&
      !can(
        sub.requiredPermission.resource as any,
        sub.requiredPermission.action as any,
      )
    )
      return false;
    return true;
  });

  const handleSupport = (sub: SubItem) => {
    if (sub.path === "#tour") {
      const tourId = tourForRoute(location.pathname);
      resetTour(tourId);
      startTour(tourId);
    } else {
      navigate(sub.path);
    }
  };

  return (
    <TooltipProvider delayDuration={200} skipDelayDuration={300}>
      {/* Spacer reserves solid width only when pinned; hover expands as an overlay (no layout push). */}
      <div
        className={cn(
          "relative z-40 h-svh shrink-0",
          WIDTH_TRANSITION,
          pinned ? "w-[236px]" : "w-[55px]",
        )}
      >
        <aside
          data-tour="sidebar-nav"
          onMouseEnter={triggerHover}
          onMouseMove={triggerHover}
          onMouseLeave={onLeave}
          style={overlay ? { zIndex: 1000 } : undefined}
          className={cn(
            "absolute inset-y-0 left-0 flex h-svh flex-col overflow-hidden border-r border-rail-border bg-rail sidebar-gradient text-rail-foreground",
            WIDTH_TRANSITION,
            expanded ? "w-[236px]" : "w-[55px]",
            overlay && "sidebar-shadow",
          )}
        >
          <div className="flex h-full w-full flex-col">
            <div className="px-2">
              <SidebarHeader
                expanded={expanded}
                pinned={pinned}
                togglePinned={toggle}
              />
            </div>

            {/* Scope + primary nav (scrollable) */}
            <div className="no-scrollbar flex min-h-0 flex-1 flex-col gap-px overflow-y-auto overflow-x-hidden px-2 py-1 overscroll-contain">
              <ScopeSwitcherRail
                expanded={expanded}
                onOpenChange={handleSwitcherOpenChange}
              />
              <div className="h-1.5 shrink-0" />
              {navSections.map((section, i) => (
                <NavSectionBlock
                  key={section.title ?? `s-${i}`}
                  section={section}
                  expanded={expanded}
                  onItemHover={triggerHover}
                />
              ))}
              <div
                className={cn(
                  "mt-3 px-1 pb-6 transition-opacity duration-200",
                  expanded ? "opacity-100" : "opacity-0 pointer-events-none"
                )}
              >
                <div className="group relative overflow-hidden rounded-xl border border-rail-border/40 bg-black/20 shadow-sm transition-all hover:border-rail-border/80">
                  <img
                    src={shipFasterImg}
                    alt="Ship better software, faster"
                    className="h-auto w-full rounded-xl object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex shrink-0 flex-col gap-px px-2 pb-2 pt-1">
              <div className="mx-1 mb-1 h-px bg-rail-border" />

              <FooterGroup
                icon={Settings}
                title="Configuration"
                subs={configSubs as SubItem[]}
                expanded={expanded}
                fallbackPath={config.path}
                onSelect={(sub) => navigate(sub.path)}
                tourId="sidebar-settings"
                onHover={triggerHover}
              />
              <FooterGroup
                icon={LifeBuoy}
                title="Support"
                subs={SupportMenuItems as SubItem[]}
                expanded={expanded}
                fallbackPath="/support"
                onSelect={handleSupport}
                onHover={triggerHover}
              />
              <NotificationsRow expanded={expanded} />
              <ThemeRow expanded={expanded} />
              <div className="mx-1 my-1 h-px bg-rail-border" />
              <UserRow
                expanded={expanded}
                onMenuOpenChange={handleProfileOpenChange}
              />
            </div>
          </div>
        </aside>
      </div>
    </TooltipProvider>
  );
}

export default SmartSidebar;
